import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Autocomplete,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  Link,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  AddRounded as AddRoundedIcon,
  CloseRounded as CloseRoundedIcon,
  CloudUploadRounded as CloudUploadRoundedIcon,
  DeleteOutlineRounded as DeleteOutlineRoundedIcon,
  DirectionsCarFilledRounded as DirectionsCarFilledRoundedIcon,
  DownloadRounded as DownloadRoundedIcon,
  EditOutlined as EditOutlinedIcon,
  ImageOutlined as ImageOutlinedIcon,
  InsertChartRounded as InsertChartRoundedIcon,
  Inventory2Outlined as Inventory2OutlinedIcon,
  PaidOutlined as PaidOutlinedIcon,
  ReceiptLongRounded as ReceiptLongRoundedIcon,
  RefreshRounded as RefreshRoundedIcon,
  SearchRounded as SearchRoundedIcon,
  UploadFileRounded as UploadFileRoundedIcon,
  VisibilityOutlined as VisibilityOutlinedIcon,
} from "@mui/icons-material";
import FlexibleStatsRow from "../common/FlexibleStatsRow";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
  PieChart,
  Pie,
  Cell,
  Legend,
} from "recharts";
import * as XLSX from "xlsx";

import { useGetAllLiteDevicesQuery } from "../../redux/admin/adminApi";
import {
  useAddExpenseMutation,
  useDeleteExpenseMutation,
  useGetExpensesQuery,
  useUpdateExpenseMutation,
} from "../../redux/logistics/logisticsApi";
import { useStatus } from "../../popup/NotificationContext";

const EXPENSE_TYPES = ["Fuel", "Food", "Service", "Tools", "Parts", "Hotel", "Toll", "Other"];
const PAYMENT_MODES = ["Cash", "Card", "Online"];
const PAGE_SECTIONS = [
  { key: "create", label: "Create Expense" },
  { key: "list", label: "Expense List" },
  { key: "upload", label: "Upload Expense" },
];
const CHART_COLORS = ["#2563eb", "#34d399", "#f59e0b", "#fb7185", "#8b5cf6", "#94a3b8"];

const inputSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: 2.5,
    backgroundColor: "#ffffff",
    minHeight: 52,
  },
};

const softCardSx = {
  borderRadius: 4,
  border: "1px solid rgba(148,163,184,0.16)",
  boxShadow: "0 18px 40px rgba(15,23,42,0.05)",
  background: "#ffffff",
};

const toYmd = (value) => {
  const date = value ? new Date(value) : new Date();
  if (Number.isNaN(date.getTime())) return "";
  return `${date.getFullYear()}-${`${date.getMonth() + 1}`.padStart(2, "0")}-${`${date.getDate()}`.padStart(2, "0")}`;
};

const startOfMonthYmd = () => {
  const now = new Date();
  return `${now.getFullYear()}-${`${now.getMonth() + 1}`.padStart(2, "0")}-01`;
};

const formatCurrency = (value) =>
  `₹ ${Number(value || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const parseExpenses = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.expenses)) return payload.expenses;
  return [];
};

const parseDevices = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  return [];
};

const getDevicePlateLabel = (vehicle = {}) =>
  vehicle?.vehicleNumber ||
  vehicle?.plateNumber ||
  vehicle?.plateNo ||
  vehicle?.registrationNo ||
  vehicle?.registrationNumber ||
  "";

const getVehicleLabel = (vehicle = {}) =>
  getDevicePlateLabel(vehicle) ||
  vehicle?.name ||
  vehicle?.deviceId ||
  vehicle?.model ||
  vehicle?.imei ||
  "Vehicle";

const normalizeExpenseRow = (expense) => {
  const vehicle = expense?.vehicle || {};
  const amount = Number(expense?.amount || 0);
  const quantity = Number(expense?.quantity || 0);
  return {
    id: expense?._id,
    raw: expense,
    date: expense?.date || expense?.createdAt,
    dateLabel: expense?.date ? new Date(expense.date).toLocaleString("en-GB") : "-",
    vehicleId: vehicle?._id || expense?.vehicle || "",
    vehicleNo: getDevicePlateLabel(vehicle) || "-",
    vehicleName: vehicle?.name || vehicle?.model || getVehicleLabel(vehicle),
    odometer: Number(vehicle?.totalDistance || 0),
    type: expense?.type || "-",
    quantity: Number.isFinite(quantity) && quantity > 0 ? quantity : "",
    mileage: expense?.mileage || "",
    model: vehicle?.model || "-",
    amount,
    average: quantity > 0 ? Number((amount / quantity).toFixed(2)) : 0,
    paymentMode: expense?.paymentMode || "-",
    description: expense?.description || "-",
    image: expense?.receiptPath || "",
    createdBy: expense?.createdBy?.username || "",
  };
};

const buildCsv = (rows) => {
  const headers = [
    "Date",
    "Vehicle No",
    "Odometer",
    "Type",
    "Qty",
    "Mileage",
    "Model",
    "Amount",
    "Avg/Unit",
    "Payment Mode",
    "Description",
    "Image",
  ];

  const lines = rows.map((row) =>
    [
      row.dateLabel,
      row.vehicleNo,
      row.odometer,
      row.type,
      row.quantity,
      row.mileage,
      row.model,
      row.amount,
      row.average,
      row.paymentMode,
      row.description,
      row.image,
    ]
      .map((value) => `"${String(value ?? "").replace(/"/g, '""')}"`)
      .join(",")
  );

  return [headers.join(","), ...lines].join("\n");
};

const buildUploadTemplate = () => {
  const rows = [
    {
      Date: "17/05/2026",
      "Vehicle No": "HR55AB1234",
      Type: "Fuel",
      Description: "Diesel fillup",
      Quantity: "45.00",
      Amount: "2850.00",
      "Payment Mode": "Card",
    },
  ];

  const worksheet = XLSX.utils.json_to_sheet(rows);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Expenses");
  return XLSX.write(workbook, { bookType: "xlsx", type: "array" });
};

const getStatusColor = (value) => {
  const status = String(value || "").toLowerCase();
  if (status.includes("valid")) return { bg: "#dcfce7", fg: "#166534" };
  if (status.includes("invalid")) return { bg: "#fee2e2", fg: "#b91c1c" };
  return { bg: "#e2e8f0", fg: "#475569" };
};

const emptyState = (onCreate) => (
  <Stack alignItems="center" justifyContent="center" spacing={1.2} sx={{ py: 7, color: "#64748b" }}>
    <Avatar sx={{ width: 92, height: 92, bgcolor: "#eef2ff", color: "#2563eb" }}>
      <ReceiptLongRoundedIcon sx={{ fontSize: 46 }} />
    </Avatar>
    <Typography sx={{ fontSize: 22, fontWeight: 800, color: "#0f172a" }}>No expenses found</Typography>
    <Typography sx={{ textAlign: "center", maxWidth: 380 }}>
      Start by creating your first expense entry and it will appear here with filters, graphs, and export support.
    </Typography>
    {onCreate ? (
      <Button
        variant="contained"
        startIcon={<AddRoundedIcon />}
        onClick={onCreate}
        sx={{ mt: 1, borderRadius: 3, px: 2.5, py: 1.2, textTransform: "none", fontWeight: 800 }}
      >
        Create Expense
      </Button>
    ) : null}
  </Stack>
);

const getDeviceSearchKeys = (device) =>
  [
    device?._id,
    device?.vehicleNumber,
    device?.registrationNo,
    device?.deviceId,
    device?.imei,
    device?.model,
    device?.name,
  ]
    .filter(Boolean)
    .map((value) => String(value).trim().toUpperCase());

const createExpenseFormData = (values) => {
  const fd = new FormData();
  fd.append("vehicle", values.vehicleId);
  fd.append("type", values.type);
  fd.append("description", values.description || "");
  fd.append("date", values.date);
  fd.append("amount", values.amount);
  if (values.quantity) fd.append("quantity", values.quantity);
  if (values.paymentMode) fd.append("paymentMode", values.paymentMode);
  if (values.file) fd.append("file", values.file);
  return fd;
};

const getInitialFormState = () => ({
  vehicle: null,
  type: "",
  description: "",
  date: toYmd(new Date()),
  amount: "",
  quantity: "1",
  paymentMode: "",
  file: null,
});

const getInitialEditState = () => ({
  id: "",
  vehicle: null,
  type: "",
  description: "",
  date: "",
  amount: "",
  quantity: "",
  paymentMode: "",
  file: null,
});

const SECTION_META = {
  create: {
    title: "Create Expense",
    subtitle: "Add a new expense for your vehicle",
  },
  list: {
    title: "Expense List",
    subtitle: "Track, review and export all vehicle expenses",
  },
  upload: {
    title: "Upload Expense",
    subtitle: "Upload bulk expenses using Excel or CSV files",
  },
};

const ExpensePortalModal = ({ open = true, onClose, asPage = false, section = "list" }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const fileInputRef = useRef(null);
  const importFileInputRef = useRef(null);
  const { showStatus } = useStatus();
  const { data: expensesPayload, refetch } = useGetExpensesQuery();
  const { data: devicesPayload, refetch: refetchDevices } = useGetAllLiteDevicesQuery();
  const [addExpense, { isLoading: isSaving }] = useAddExpenseMutation();
  const [updateExpense, { isLoading: isUpdating }] = useUpdateExpenseMutation();
  const [deleteExpense, { isLoading: isDeleting }] = useDeleteExpenseMutation();

  const devices = useMemo(() => parseDevices(devicesPayload), [devicesPayload]);
  const expenses = useMemo(() => parseExpenses(expensesPayload).map(normalizeExpenseRow), [expensesPayload]);
  const deviceOptions = useMemo(
    () =>
      devices.map((device) => ({
        ...device,
        searchKeys: getDeviceSearchKeys(device),
      })),
    [devices]
  );

  const [activeSection, setActiveSection] = useState(section);
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [fromDate, setFromDate] = useState(startOfMonthYmd());
  const [toDate, setToDate] = useState(toYmd(new Date()));
  const [searchTerm, setSearchTerm] = useState("");
  const [createForm, setCreateForm] = useState(getInitialFormState);
  const [editState, setEditState] = useState(getInitialEditState);
  const [viewExpense, setViewExpense] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [vehicleSearch, setVehicleSearch] = useState("");
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadPreview, setUploadPreview] = useState([]);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    setActiveSection(section);
  }, [section]);

  useEffect(() => {
    setPage(1);
  }, [activeSection, rowsPerPage, searchTerm, selectedVehicle, fromDate, toDate]);

  const overallStats = useMemo(() => {
    const now = new Date();
    const monthKey = `${now.getFullYear()}-${now.getMonth()}`;
    const todayKey = now.toDateString();

    let todayExpense = 0;
    let monthExpense = 0;
    let monthTransactions = 0;

    expenses.forEach((row) => {
      const date = new Date(row.date);
      if (Number.isNaN(date.getTime())) return;
      if (date.toDateString() === todayKey) todayExpense += Number(row.amount || 0);
      if (`${date.getFullYear()}-${date.getMonth()}` === monthKey) {
        monthExpense += Number(row.amount || 0);
        monthTransactions += 1;
      }
    });

    return {
      totalExpense: expenses.reduce((sum, row) => sum + Number(row.amount || 0), 0),
      totalTransactions: expenses.length,
      averageExpense: expenses.length ? expenses.reduce((sum, row) => sum + Number(row.amount || 0), 0) / expenses.length : 0,
      totalVehicles: new Set(expenses.map((row) => row.vehicleId).filter(Boolean)).size,
      todayExpense,
      monthExpense,
      monthTransactions,
    };
  }, [expenses]);

  const summaryCards = useMemo(
    () => [
      {
        key: "expense",
        label: "Total Expense",
        value: formatCurrency(overallStats.totalExpense),
        detail: "This month",
        tone: "danger",
        icon: <PaidOutlinedIcon fontSize="small" />,
      },
      {
        key: "transactions",
        label: "Total Transactions",
        value: overallStats.totalTransactions,
        detail: "This month",
        tone: "success",
        icon: <ReceiptLongRoundedIcon fontSize="small" />,
      },
      {
        key: "average",
        label: "Average Expense",
        value: formatCurrency(overallStats.averageExpense),
        detail: "Per transaction",
        tone: "warning",
        icon: <Inventory2OutlinedIcon fontSize="small" />,
      },
      {
        key: "vehicles",
        label: "Total Vehicles",
        value: overallStats.totalVehicles,
        detail: "Active",
        tone: "violet",
        icon: <DirectionsCarFilledRoundedIcon fontSize="small" />,
      },
    ],
    [overallStats]
  );

  const filteredRows = useMemo(() => {
    const fromTs = fromDate ? new Date(`${fromDate}T00:00:00`).getTime() : 0;
    const toTs = toDate ? new Date(`${toDate}T23:59:59`).getTime() : Number.MAX_SAFE_INTEGER;
    const search = searchTerm.trim().toLowerCase();

    return expenses.filter((row) => {
      const rowTs = new Date(row.date).getTime();
      if (selectedVehicle && String(row.vehicleId) !== String(selectedVehicle._id)) return false;
      if (Number.isFinite(rowTs) && (rowTs < fromTs || rowTs > toTs)) return false;
      if (!search) return true;
      return [
        row.vehicleNo,
        row.type,
        row.description,
        row.model,
        row.paymentMode,
        row.dateLabel,
      ]
        .join(" ")
        .toLowerCase()
        .includes(search);
    });
  }, [expenses, fromDate, searchTerm, selectedVehicle, toDate]);

  const totalPages = Math.max(1, Math.ceil(filteredRows.length / rowsPerPage));
  const pagedRows = useMemo(() => {
    const start = (page - 1) * rowsPerPage;
    return filteredRows.slice(start, start + rowsPerPage);
  }, [filteredRows, page, rowsPerPage]);

  const expenseTrendData = useMemo(() => {
    const bucket = new Map();
    filteredRows.forEach((row) => {
      const date = new Date(row.date);
      if (Number.isNaN(date.getTime())) return;
      const key = date.toLocaleDateString("en-IN", { day: "2-digit", month: "short" });
      bucket.set(key, (bucket.get(key) || 0) + Number(row.amount || 0));
    });
    return Array.from(bucket.entries()).map(([name, amount]) => ({ name, amount }));
  }, [filteredRows]);

  const expenseByType = useMemo(() => {
    const bucket = new Map();
    filteredRows.forEach((row) => {
      const key = row.type || "Other";
      bucket.set(key, (bucket.get(key) || 0) + Number(row.amount || 0));
    });
    return Array.from(bucket.entries()).map(([name, value]) => ({ name, value }));
  }, [filteredRows]);

  const vehicleList = useMemo(() => {
    const search = vehicleSearch.trim().toLowerCase();
    return deviceOptions.filter((device) => {
      if (!search) return true;
      return [getVehicleLabel(device), device?.model, device?.imei, device?.registrationNo]
        .join(" ")
        .toLowerCase()
        .includes(search);
    });
  }, [deviceOptions, vehicleSearch]);

  const uploadStats = useMemo(() => {
    const validRows = uploadPreview.filter((row) => row.isValid).length;
    return {
      total: uploadPreview.length,
      valid: validRows,
      invalid: uploadPreview.length - validRows,
    };
  }, [uploadPreview]);

  const uploadSummaryCards = useMemo(
    () => [
      {
        key: "totalRows",
        label: "Total Rows",
        value: uploadStats.total,
        detail: "Preview loaded",
        tone: "neutral",
        icon: <ReceiptLongRoundedIcon fontSize="small" />,
      },
      {
        key: "validRows",
        label: "Valid Rows",
        value: uploadStats.valid,
        detail: "Ready to import",
        tone: "success",
        icon: <CloudUploadRoundedIcon fontSize="small" />,
      },
      {
        key: "invalidRows",
        label: "Invalid Rows",
        value: uploadStats.invalid,
        detail: "Needs correction",
        tone: "danger",
        icon: <CloseRoundedIcon fontSize="small" />,
      },
    ],
    [uploadStats]
  );

  const meta = SECTION_META[activeSection] || SECTION_META.list;

  const resetCreateForm = () => setCreateForm(getInitialFormState());

  const downloadCsv = (rows = filteredRows) => {
    const csv = buildCsv(rows);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `expenses-${fromDate || "from"}-${toDate || "to"}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    showStatus("success", "Expense CSV downloaded.");
  };

  const handleCreateExpense = async () => {
    if (!createForm.vehicle?._id || !createForm.type || !createForm.date || !createForm.amount) {
      showStatus("warning", "Vehicle, type, date and amount are required.");
      return;
    }

    try {
      const response = await addExpense(
        createExpenseFormData({
          vehicleId: createForm.vehicle._id,
          type: createForm.type,
          description: createForm.description,
          date: createForm.date,
          amount: createForm.amount,
          quantity: createForm.quantity,
          paymentMode: createForm.paymentMode,
          file: createForm.file,
        })
      ).unwrap();
      await refetch();
      showStatus(
        "success",
        response?.isDemoLocalOnly
          ? "Demo mode: Expense saved in this browser so you can test the workflow. Live billing data was not changed."
          : "Expense saved successfully."
      );
      resetCreateForm();
      if (!asPage) setActiveSection("list");
    } catch (err) {
      showStatus("error", err?.data?.error || "Failed to save expense.");
    }
  };

  const openEditDialog = (row) => {
    const raw = row.raw || {};
    const matchedVehicle =
      deviceOptions.find((device) => String(device._id) === String(row.vehicleId)) || null;

    setEditState({
      id: row.id,
      vehicle: matchedVehicle,
      type: raw?.type || row.type || "",
      description: raw?.description || row.description || "",
      date: raw?.date ? toYmd(raw.date) : toYmd(row.date),
      amount: raw?.amount || row.amount || "",
      quantity: raw?.quantity || row.quantity || "",
      paymentMode: raw?.paymentMode || row.paymentMode || "",
      file: null,
    });
  };

  const handleUpdateExpense = async () => {
    if (!editState.id || !editState.vehicle?._id || !editState.type || !editState.date || !editState.amount) {
      showStatus("warning", "Vehicle, type, date and amount are required.");
      return;
    }

    try {
      await updateExpense({
        id: editState.id,
        body: createExpenseFormData({
          vehicleId: editState.vehicle._id,
          type: editState.type,
          description: editState.description,
          date: editState.date,
          amount: editState.amount,
          quantity: editState.quantity,
          paymentMode: editState.paymentMode,
          file: editState.file,
        }),
      }).unwrap();
      await refetch();
      showStatus("success", "Expense updated successfully.");
      setEditState(getInitialEditState());
    } catch (err) {
      showStatus("error", err?.data?.error || "Failed to update expense.");
    }
  };

  const handleDeleteExpense = async () => {
    if (!deleteTarget?.id) return;
    try {
      await deleteExpense(deleteTarget.id).unwrap();
      await refetch();
      showStatus("success", "Expense deleted successfully.");
      setDeleteTarget(null);
    } catch (err) {
      showStatus("error", err?.data?.error || "Failed to delete expense.");
    }
  };

  const downloadTemplate = () => {
    const content = buildUploadTemplate();
    const blob = new Blob([content], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "expense-upload-template.xlsx";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const mapUploadRows = (rows) => {
    const normalizedDevices = deviceOptions.map((device) => ({
      ...device,
      match: getDeviceSearchKeys(device),
    }));

    return rows.map((row, index) => {
      const date = row.Date || row.date || row.DATE || "";
      const vehicleText = row["Vehicle No"] || row.vehicle || row["vehicle no"] || row.Vehicle || "";
      const type = row.Type || row.type || "";
      const description = row.Description || row.description || "";
      const quantity = row.Quantity || row.quantity || "";
      const amount = row.Amount || row.amount || "";
      const paymentMode = row["Payment Mode"] || row.paymentMode || row["Payment mode"] || "";

      const matchKey = String(vehicleText || "").trim().toUpperCase();
      const vehicle = normalizedDevices.find((device) => device.match.includes(matchKey)) || null;
      const amountNumber = Number(amount);
      const typeMatch = EXPENSE_TYPES.some((item) => item.toLowerCase() === String(type).toLowerCase());

      const errors = [];
      if (!date) errors.push("Date missing");
      if (!vehicle?._id) errors.push("Vehicle not matched");
      if (!typeMatch) errors.push("Invalid type");
      if (!amount || Number.isNaN(amountNumber)) errors.push("Invalid amount");

      return {
        id: `${index}-${vehicleText}-${type}`,
        date: String(date || ""),
        vehicleNo: String(vehicleText || ""),
        vehicle,
        type: String(type || ""),
        description: String(description || ""),
        quantity: String(quantity || ""),
        amount: String(amount || ""),
        paymentMode: String(paymentMode || ""),
        isValid: errors.length === 0,
        errors,
      };
    });
  };

  const parseUploadFile = async (file) => {
    const buffer = await file.arrayBuffer();
    const workbook = XLSX.read(buffer, { type: "array" });
    const sheetName = workbook.SheetNames[0];
    const worksheet = workbook.Sheets[sheetName];
    const rows = XLSX.utils.sheet_to_json(worksheet, { defval: "" });
    setUploadPreview(mapUploadRows(rows));
  };

  const handleUploadFile = async (file) => {
    if (!file) return;
    setUploadFile(file);
    try {
      await parseUploadFile(file);
    } catch (err) {
      console.error(err);
      showStatus("error", "Failed to parse uploaded file.");
    }
  };

  const importExpenses = async () => {
    const validRows = uploadPreview.filter((row) => row.isValid);
    if (!validRows.length) {
      showStatus("warning", "No valid rows available for import.");
      return;
    }

    setUploading(true);
    try {
      let usedDemoLocalMode = false;
      for (const row of validRows) {
        const response = await addExpense(
          createExpenseFormData({
            vehicleId: row.vehicle._id,
            type: row.type,
            description: row.description,
            date: row.date.includes("/") ? row.date.split("/").reverse().join("-") : row.date,
            amount: row.amount,
            quantity: row.quantity,
            paymentMode: row.paymentMode,
          })
        ).unwrap();
        if (response?.isDemoLocalOnly) {
          usedDemoLocalMode = true;
        }
      }

      await refetch();
      showStatus(
        "success",
        usedDemoLocalMode
          ? `Demo mode: ${validRows.length} expenses were saved in this browser for walkthrough purposes only.`
          : `${validRows.length} expenses imported successfully.`
      );
      setUploadFile(null);
      setUploadPreview([]);
    } catch (err) {
      console.error(err);
      showStatus("error", err?.data?.error || "Failed to import expenses.");
    } finally {
      setUploading(false);
    }
  };

  const renderCreatePage = () => (
    <Stack spacing={2.5}>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", xl: "minmax(0,1.8fr) minmax(280px,0.58fr) minmax(300px,0.72fr)" },
          gap: 2.5,
          alignItems: "start",
        }}
      >
        <Paper sx={{ ...softCardSx, p: { xs: 2, md: 2.5 } }}>
          <Stack spacing={2.5}>
            <Typography sx={{ fontSize: 23, fontWeight: 900, color: "#0f172a" }}>
              Expense Information
            </Typography>
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr" }, gap: 2 }}>
              <Autocomplete
                options={deviceOptions}
                value={createForm.vehicle}
                onChange={(_, value) => setCreateForm((prev) => ({ ...prev, vehicle: value }))}
                getOptionLabel={(option) => getVehicleLabel(option)}
                renderInput={(params) => <TextField {...params} label="Vehicle *" placeholder="Select vehicle" sx={inputSx} />}
              />
              <FormControl fullWidth sx={inputSx}>
                <InputLabel>Type *</InputLabel>
                <Select
                  label="Type *"
                  value={createForm.type}
                  onChange={(e) => setCreateForm((prev) => ({ ...prev, type: e.target.value }))}
                >
                  {EXPENSE_TYPES.map((type) => (
                    <MenuItem key={type} value={type}>
                      {type}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label="Description"
                value={createForm.description}
                onChange={(e) => setCreateForm((prev) => ({ ...prev, description: e.target.value }))}
                sx={inputSx}
              />
              <TextField
                type="date"
                label="Date *"
                value={createForm.date}
                InputLabelProps={{ shrink: true }}
                onChange={(e) => setCreateForm((prev) => ({ ...prev, date: e.target.value }))}
                sx={inputSx}
              />
              <TextField
                label="Amount *"
                type="number"
                value={createForm.amount}
                onChange={(e) => setCreateForm((prev) => ({ ...prev, amount: e.target.value }))}
                sx={inputSx}
                InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
              />
              <TextField
                label="Quantity"
                type="number"
                value={createForm.quantity}
                onChange={(e) => setCreateForm((prev) => ({ ...prev, quantity: e.target.value }))}
                sx={inputSx}
              />
              <FormControl fullWidth sx={inputSx}>
                <InputLabel>Mode of Payment</InputLabel>
                <Select
                  label="Mode of Payment"
                  value={createForm.paymentMode}
                  onChange={(e) => setCreateForm((prev) => ({ ...prev, paymentMode: e.target.value }))}
                >
                  {PAYMENT_MODES.map((modeValue) => (
                    <MenuItem key={modeValue} value={modeValue}>
                      {modeValue}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Box sx={{ gridColumn: { xs: "auto", md: "span 2" } }}>
                <Paper
                  variant="outlined"
                  onClick={() => fileInputRef.current?.click()}
                  sx={{
                    ...softCardSx,
                    p: 2,
                    borderStyle: "dashed",
                    borderColor: "rgba(37,99,235,0.28)",
                    cursor: "pointer",
                    display: "grid",
                    placeItems: "center",
                    minHeight: 116,
                    background: "#f8fbff",
                  }}
                >
                  <Stack spacing={0.8} alignItems="center">
                    <Avatar sx={{ bgcolor: "#e0ecff", color: "#2563eb" }}>
                      <CloudUploadRoundedIcon />
                    </Avatar>
                    <Typography sx={{ fontWeight: 800, color: "#0f172a" }}>
                      {createForm.file ? createForm.file.name : "Upload Receipt / Image"}
                    </Typography>
                    <Typography sx={{ color: "#64748b", fontSize: 13 }}>
                      Click to upload or drag & drop PNG, JPG, JPEG (Max. 5MB)
                    </Typography>
                  </Stack>
                </Paper>
                <input
                  ref={fileInputRef}
                  type="file"
                  hidden
                  accept="image/*,.pdf"
                  onChange={(e) => setCreateForm((prev) => ({ ...prev, file: e.target.files?.[0] || null }))}
                />
              </Box>
            </Box>
            <Alert severity="info" sx={{ borderRadius: 3 }}>
              Please ensure all information is correct before submitting the expense.
            </Alert>
            <Stack direction="row" spacing={1.5}>
              <Button
                variant="contained"
                onClick={handleCreateExpense}
                disabled={isSaving}
                sx={{ borderRadius: 3, px: 2.4, py: 1.2, textTransform: "none", fontWeight: 800 }}
              >
                {isSaving ? "Submitting..." : "Submit Expense"}
              </Button>
              <Button
                variant="outlined"
                onClick={resetCreateForm}
                sx={{ borderRadius: 3, px: 2.4, py: 1.2, textTransform: "none", fontWeight: 800 }}
              >
                Cancel
              </Button>
            </Stack>
          </Stack>
        </Paper>

        <Paper sx={{ ...softCardSx, p: { xs: 2, md: 2.5 }, background: "linear-gradient(180deg,#f8fbff 0%, #ffffff 100%)" }}>
          <Stack spacing={2.3}>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Avatar sx={{ bgcolor: "#fee2e2", color: "#ef4444" }}>
                <ReceiptLongRoundedIcon />
              </Avatar>
              <Typography sx={{ fontSize: 19, fontWeight: 900, color: "#0f172a" }}>Expense Summary</Typography>
            </Stack>
            <Box>
              <Typography sx={{ color: "#64748b", fontSize: 13 }}>Today's Expense</Typography>
              <Typography sx={{ fontSize: 26, fontWeight: 900, color: "#0f172a", mt: 0.6 }}>
                {formatCurrency(overallStats.todayExpense)}
              </Typography>
            </Box>
            <Divider />
            <Box>
              <Typography sx={{ color: "#64748b", fontSize: 13 }}>This Month</Typography>
              <Typography sx={{ fontSize: 26, fontWeight: 900, color: "#0f172a", mt: 0.6 }}>
                {formatCurrency(overallStats.monthExpense)}
              </Typography>
            </Box>
            <Divider />
            <Box>
              <Typography sx={{ color: "#64748b", fontSize: 13 }}>This Month Transactions</Typography>
              <Typography sx={{ fontSize: 26, fontWeight: 900, color: "#0f172a", mt: 0.6 }}>
                {overallStats.monthTransactions}
              </Typography>
            </Box>
            <Divider />
            <Box>
              <Typography sx={{ color: "#64748b", fontSize: 13 }}>Average Expense</Typography>
              <Typography sx={{ fontSize: 26, fontWeight: 900, color: "#0f172a", mt: 0.6 }}>
                {formatCurrency(overallStats.averageExpense)}
              </Typography>
            </Box>
          </Stack>
        </Paper>

        <Paper sx={{ ...softCardSx, p: { xs: 2, md: 2.5 } }}>
          <Stack spacing={2}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Box>
                <Typography sx={{ fontSize: 28, fontWeight: 900, color: "#0f172a" }}>Vehicle List</Typography>
                <Typography sx={{ color: "#64748b", fontSize: 14 }}>
                  Select a vehicle to add expense
                </Typography>
              </Box>
              <Tooltip title="Refresh vehicles">
                <IconButton onClick={() => refetchDevices()}>
                  <RefreshRoundedIcon />
                </IconButton>
              </Tooltip>
            </Stack>
            <TextField
              placeholder="Search vehicle..."
              value={vehicleSearch}
              onChange={(e) => setVehicleSearch(e.target.value)}
              sx={inputSx}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <SearchRoundedIcon sx={{ color: "#94a3b8" }} />
                  </InputAdornment>
                ),
              }}
            />
            <Stack spacing={1.2} sx={{ maxHeight: 430, overflowY: "auto", pr: 0.5 }}>
              {vehicleList.map((vehicle) => {
                const active = createForm.vehicle?._id === vehicle._id;
                return (
                  <Paper
                    key={vehicle._id}
                    onClick={() => setCreateForm((prev) => ({ ...prev, vehicle }))}
                    sx={{
                      p: 1.6,
                      borderRadius: 3,
                      border: active ? "1px solid rgba(37,99,235,0.34)" : "1px solid rgba(148,163,184,0.14)",
                      boxShadow: active ? "0 12px 24px rgba(37,99,235,0.12)" : "none",
                      cursor: "pointer",
                    }}
                  >
                    <Stack direction="row" spacing={1.2} alignItems="center">
                      <Avatar sx={{ bgcolor: "#e0ecff", color: "#2563eb", width: 40, height: 40 }}>
                        <DirectionsCarFilledRoundedIcon fontSize="small" />
                      </Avatar>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography noWrap sx={{ fontWeight: 800, color: "#0f172a" }}>
                          {getVehicleLabel(vehicle)}
                        </Typography>
                        <Typography noWrap sx={{ color: "#64748b", fontSize: 13 }}>
                          {vehicle?.model || vehicle?.imei || "Vehicle"}
                        </Typography>
                      </Box>
                      <Chip
                        label={vehicle?.isActive === false ? "Inactive" : "Active"}
                        size="small"
                        sx={{
                          bgcolor: vehicle?.isActive === false ? "#fef3c7" : "#dcfce7",
                          color: vehicle?.isActive === false ? "#b45309" : "#15803d",
                          fontWeight: 700,
                        }}
                      />
                    </Stack>
                  </Paper>
                );
              })}
            </Stack>
            <Box sx={{ textAlign: "right" }}>
              <Button variant="text" sx={{ textTransform: "none", fontWeight: 800 }}>
                View all vehicles
              </Button>
            </Box>
          </Stack>
        </Paper>
      </Box>
    </Stack>
  );

  const renderExpenseRowActions = (row) => (
    <Stack direction="row" spacing={0.8} justifyContent="flex-end">
      <Tooltip title="View">
        <IconButton
          onClick={() => setViewExpense(row)}
          sx={{ width: 36, height: 36, borderRadius: 2.5, bgcolor: "#dbeafe", color: "#2563eb" }}
        >
          <VisibilityOutlinedIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title="Edit">
        <IconButton
          onClick={() => openEditDialog(row)}
          sx={{ width: 36, height: 36, borderRadius: 2.5, bgcolor: "#dbeafe", color: "#2563eb" }}
        >
          <EditOutlinedIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title="Delete">
        <IconButton
          onClick={() => setDeleteTarget(row)}
          sx={{ width: 36, height: 36, borderRadius: 2.5, bgcolor: "#fee2e2", color: "#ef4444" }}
        >
          <DeleteOutlineRoundedIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    </Stack>
  );

  const renderListPage = () => (
    <Stack spacing={2.5}>
      <FlexibleStatsRow items={summaryCards} />

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "1.08fr 0.92fr" }, gap: 1.4 }}>
        <Paper sx={{ ...softCardSx, p: { xs: 2, lg: 2.1 } }}>
          <Stack spacing={1.8}>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Typography sx={{ fontSize: 18, fontWeight: 900, color: "#0f172a" }}>Expense Overview</Typography>
              <Chip label="This Month" sx={{ bgcolor: "#eff6ff", color: "#1d4ed8", fontWeight: 700 }} />
            </Stack>
            <Box sx={{ height: { xs: 240, lg: 208 } }}>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={expenseTrendData}>
                  <defs>
                    <linearGradient id="expenseArea" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#2563eb" stopOpacity={0.26} />
                      <stop offset="95%" stopColor="#2563eb" stopOpacity={0.03} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid stroke="#e2e8f0" vertical={false} />
                  <XAxis dataKey="name" tick={{ fill: "#64748b", fontSize: 12 }} />
                  <YAxis tick={{ fill: "#64748b", fontSize: 12 }} />
                  <RechartsTooltip />
                  <Area type="monotone" dataKey="amount" stroke="#2563eb" strokeWidth={3} fill="url(#expenseArea)" />
                </AreaChart>
              </ResponsiveContainer>
            </Box>
          </Stack>
        </Paper>

        <Paper sx={{ ...softCardSx, p: { xs: 2, lg: 2.1 } }}>
          <Stack spacing={1.8}>
            <Typography sx={{ fontSize: 18, fontWeight: 900, color: "#0f172a" }}>Expense by Category</Typography>
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "0.9fr 1.1fr" }, gap: 1.5, alignItems: "center" }}>
              <Box sx={{ height: { xs: 240, lg: 208 } }}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie data={expenseByType} dataKey="value" nameKey="name" innerRadius={74} outerRadius={112}>
                      {expenseByType.map((entry, index) => (
                        <Cell key={entry.name} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                      ))}
                    </Pie>
                    <RechartsTooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </Box>
              <Stack spacing={1.2}>
                {expenseByType.map((entry, index) => (
                  <Stack key={entry.name} direction="row" justifyContent="space-between" spacing={2}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Box sx={{ width: 10, height: 10, borderRadius: 99, bgcolor: CHART_COLORS[index % CHART_COLORS.length] }} />
                      <Typography sx={{ color: "#475569", fontWeight: 600 }}>{entry.name}</Typography>
                    </Stack>
                    <Typography sx={{ color: "#0f172a", fontWeight: 800 }}>{formatCurrency(entry.value)}</Typography>
                  </Stack>
                ))}
              </Stack>
            </Box>
          </Stack>
        </Paper>
      </Box>

      <Paper sx={{ ...softCardSx, p: 2.5 }}>
        <Stack spacing={2}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1.15fr 1fr 1fr 1.35fr auto auto auto auto" },
              gap: 1.4,
              alignItems: "center",
            }}
          >
            <Autocomplete
              options={deviceOptions}
              value={selectedVehicle}
              onChange={(_, value) => setSelectedVehicle(value)}
              getOptionLabel={(option) => getVehicleLabel(option)}
              renderInput={(params) => <TextField {...params} label="Vehicle" placeholder="All vehicles" sx={inputSx} />}
            />
            <TextField
              type="date"
              label="From"
              value={fromDate}
              InputLabelProps={{ shrink: true }}
              onChange={(e) => setFromDate(e.target.value)}
              sx={inputSx}
            />
            <TextField
              type="date"
              label="To"
              value={toDate}
              InputLabelProps={{ shrink: true }}
              onChange={(e) => setToDate(e.target.value)}
              sx={inputSx}
            />
            <TextField
              placeholder="Search by type, description..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              sx={inputSx}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <SearchRoundedIcon sx={{ color: "#94a3b8" }} />
                  </InputAdornment>
                ),
              }}
            />
            <Tooltip title="Refresh">
              <IconButton onClick={() => refetch()} sx={{ width: 48, height: 48, borderRadius: 3, border: "1px solid rgba(148,163,184,0.24)" }}>
                <RefreshRoundedIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Charts">
              <IconButton onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} sx={{ width: 48, height: 48, borderRadius: 3, border: "1px solid rgba(148,163,184,0.24)" }}>
                <InsertChartRoundedIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Export">
              <IconButton onClick={() => downloadCsv()} sx={{ width: 48, height: 48, borderRadius: 3, border: "1px solid rgba(148,163,184,0.24)" }}>
                <DownloadRoundedIcon />
              </IconButton>
            </Tooltip>
            <FormControl sx={{ minWidth: 88 }}>
              <Select value={rowsPerPage} onChange={(e) => setRowsPerPage(Number(e.target.value))}>
                {[10, 25, 50, 100].map((size) => (
                  <MenuItem key={size} value={size}>
                    {size}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          <Typography sx={{ color: "#64748b", fontSize: 13.5 }}>
            {selectedVehicle ? `Showing expenses for ${getVehicleLabel(selectedVehicle)}` : "Showing expenses for all vehicles"}
          </Typography>

          {isMobile ? (
            <Stack spacing={1.4}>
              {pagedRows.length ? (
                pagedRows.map((row) => (
                  <Paper key={row.id} sx={{ p: 1.8, borderRadius: 3, border: "1px solid rgba(148,163,184,0.16)" }}>
                    <Stack spacing={1}>
                      <Stack direction="row" justifyContent="space-between" spacing={1}>
                        <Typography sx={{ fontWeight: 900, color: "#0f172a" }}>{row.vehicleName}</Typography>
                        <Chip label={row.type} size="small" sx={{ bgcolor: "#eff6ff", color: "#1d4ed8", fontWeight: 700 }} />
                      </Stack>
                      <Typography sx={{ color: "#475569", fontSize: 13.5, fontWeight: 700 }}>
                        Vehicle No: {row.vehicleNo}
                      </Typography>
                      <Typography sx={{ color: "#64748b", fontSize: 13.5 }}>{row.dateLabel}</Typography>
                      <Typography sx={{ color: "#0f172a" }}>{row.description}</Typography>
                      <Stack direction="row" spacing={2} flexWrap="wrap">
                        <Typography sx={{ color: "#475569", fontSize: 13.5 }}>Amount: {formatCurrency(row.amount)}</Typography>
                        <Typography sx={{ color: "#475569", fontSize: 13.5 }}>Qty: {row.quantity || "-"}</Typography>
                        <Typography sx={{ color: "#475569", fontSize: 13.5 }}>Mode: {row.paymentMode}</Typography>
                      </Stack>
                      {renderExpenseRowActions(row)}
                    </Stack>
                  </Paper>
                ))
              ) : (
                emptyState(() => setActiveSection("create"))
              )}
            </Stack>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    {["Date", "Vehicle No", "Vehicle Name", "Odometer", "Type", "Qty", "Mileage", "Model", "Amount", "Avg/Unit", "Payment Mode", "Description", "Image", "Action"].map(
                      (head) => (
                        <TableCell key={head} sx={{ fontWeight: 800, color: "#64748b", borderBottom: "1px solid rgba(148,163,184,0.16)" }}>
                          {head}
                        </TableCell>
                      )
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pagedRows.length ? (
                    pagedRows.map((row) => (
                      <TableRow key={row.id} hover>
                        <TableCell>{row.dateLabel}</TableCell>
                        <TableCell sx={{ fontWeight: 800 }}>{row.vehicleNo}</TableCell>
                        <TableCell sx={{ fontWeight: 700 }}>{row.vehicleName}</TableCell>
                        <TableCell>{row.odometer ? `${Number(row.odometer).toLocaleString()} km` : "-"}</TableCell>
                        <TableCell>{row.type}</TableCell>
                        <TableCell>{row.quantity || "-"}</TableCell>
                        <TableCell>{row.mileage || "-"}</TableCell>
                        <TableCell>{row.model}</TableCell>
                        <TableCell sx={{ fontWeight: 800 }}>{formatCurrency(row.amount)}</TableCell>
                        <TableCell>{row.average ? formatCurrency(row.average) : "-"}</TableCell>
                        <TableCell>{row.paymentMode}</TableCell>
                        <TableCell sx={{ maxWidth: 220 }}>
                          <Typography noWrap>{row.description}</Typography>
                        </TableCell>
                        <TableCell>
                          {row.image ? (
                            <Link href={row.image} target="_blank" rel="noreferrer" underline="hover">
                              <ImageOutlinedIcon fontSize="small" />
                            </Link>
                          ) : (
                            "-"
                          )}
                        </TableCell>
                        <TableCell>{renderExpenseRowActions(row)}</TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell colSpan={14}>{emptyState(() => setActiveSection("create"))}</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}

          <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={2} flexWrap="wrap">
            <Typography sx={{ color: "#475569", fontSize: 14 }}>
              Showing {filteredRows.length ? (page - 1) * rowsPerPage + 1 : 0} to {Math.min(page * rowsPerPage, filteredRows.length)} of {filteredRows.length} entries
            </Typography>
            <Pagination page={page} count={totalPages} onChange={(_, value) => setPage(value)} shape="rounded" />
          </Stack>
        </Stack>
      </Paper>
    </Stack>
  );

  const renderUploadPage = () => (
    <Stack spacing={2.5}>
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", xl: "1.45fr 0.95fr" }, gap: 2.5 }}>
        <Paper sx={{ ...softCardSx, p: 2.5 }}>
          <Stack spacing={2}>
            <Typography sx={{ fontSize: 28, fontWeight: 900, color: "#0f172a" }}>Upload File</Typography>
            <Paper
              variant="outlined"
              onClick={() => importFileInputRef.current?.click()}
              onDragOver={(event) => event.preventDefault()}
              onDrop={(event) => {
                event.preventDefault();
                handleUploadFile(event.dataTransfer.files?.[0]);
              }}
              sx={{
                p: 3,
                borderRadius: 3,
                borderStyle: "dashed",
                borderColor: "rgba(37,99,235,0.25)",
                background: "#fbfdff",
                cursor: "pointer",
              }}
            >
              <Stack spacing={1.5} alignItems="center" justifyContent="center" sx={{ minHeight: 180 }}>
                <Avatar sx={{ width: 64, height: 64, bgcolor: "#dcfce7", color: "#16a34a" }}>
                  <UploadFileRoundedIcon sx={{ fontSize: 30 }} />
                </Avatar>
                <Typography sx={{ fontWeight: 900, color: "#0f172a" }}>
                  {uploadFile ? uploadFile.name : "Drag & drop your file here"}
                </Typography>
                <Typography sx={{ color: "#64748b", textAlign: "center" }}>
                  Supports: .csv, .xlsx, .xls (Max. 5MB)
                </Typography>
                <Button variant="contained" sx={{ borderRadius: 3, textTransform: "none", fontWeight: 800 }}>
                  Choose File
                </Button>
              </Stack>
            </Paper>
            <input
              ref={importFileInputRef}
              type="file"
              hidden
              accept=".csv,.xlsx,.xls"
              onChange={(e) => handleUploadFile(e.target.files?.[0])}
            />
            <Stack direction={{ xs: "column", md: "row" }} spacing={1.2}>
              <Button startIcon={<DownloadRoundedIcon />} variant="text" onClick={downloadTemplate} sx={{ textTransform: "none", fontWeight: 800 }}>
                Download sample template
              </Button>
              <Button startIcon={<DownloadRoundedIcon />} variant="outlined" onClick={downloadTemplate} sx={{ textTransform: "none", fontWeight: 800, borderRadius: 3 }}>
                Download Template
              </Button>
            </Stack>
          </Stack>
        </Paper>

        <Stack spacing={2.5}>
          <Paper sx={{ ...softCardSx, p: 2.5, background: "linear-gradient(180deg,#f6fff9 0%, #ffffff 100%)" }}>
            <Stack spacing={1.2}>
              <Typography sx={{ fontSize: 24, fontWeight: 900, color: "#0f172a" }}>File Guidelines</Typography>
              {[
                "File must be in .csv, .xlsx or .xls format",
                "Maximum file size: 5MB",
                "First row must contain column headers",
                "Date format: DD/MM/YYYY",
                "Amount should be a valid number",
              ].map((item) => (
                <Stack key={item} direction="row" spacing={1} alignItems="center">
                  <Box sx={{ width: 7, height: 7, borderRadius: 99, bgcolor: "#16a34a" }} />
                  <Typography sx={{ color: "#475569" }}>{item}</Typography>
                </Stack>
              ))}
            </Stack>
          </Paper>

          <Paper sx={{ ...softCardSx, p: 2.5 }}>
            <Stack spacing={1.5}>
              <Typography sx={{ fontSize: 24, fontWeight: 900, color: "#0f172a" }}>Template Guidelines</Typography>
              <TableContainer>
                <Table size="small">
                  <TableBody>
                    {[
                      ["Date (DD/MM/YYYY)", "17/05/2026"],
                      ["Vehicle No", "HR55AB1234"],
                      ["Type", "Fuel, Maintenance, Toll, Food etc."],
                      ["Description", "Diesel fillup"],
                      ["Quantity", "45.00"],
                      ["Amount", "2850.00"],
                      ["Payment Mode", "Card / Cash / Online"],
                    ].map(([label, value]) => (
                      <TableRow key={label}>
                        <TableCell sx={{ fontWeight: 700 }}>{label}</TableCell>
                        <TableCell sx={{ color: "#64748b" }}>{value}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              {uploadStats.invalid ? (
                <Alert severity="warning" sx={{ borderRadius: 3 }}>
                  Invalid rows will not be imported. Please fix the errors and try again.
                </Alert>
              ) : null}
            </Stack>
          </Paper>
        </Stack>
      </Box>

      <FlexibleStatsRow items={uploadSummaryCards} />

      <Paper sx={{ ...softCardSx, p: 2.5 }}>
        <Stack spacing={2}>
          <Typography sx={{ fontSize: 24, fontWeight: 900, color: "#0f172a" }}>Upload Preview</Typography>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  {["Date", "Vehicle No", "Type", "Description", "Qty", "Amount", "Payment Mode", "Status"].map((head) => (
                    <TableCell key={head} sx={{ fontWeight: 800, color: "#64748b" }}>
                      {head}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {uploadPreview.length ? (
                  uploadPreview.map((row) => {
                    const statusColors = getStatusColor(row.isValid ? "valid" : "invalid");
                    return (
                      <TableRow key={row.id} sx={{ bgcolor: row.isValid ? "#f8fffb" : "#fff7f7" }}>
                        <TableCell>{row.date}</TableCell>
                        <TableCell>{row.vehicleNo || "-"}</TableCell>
                        <TableCell>{row.type}</TableCell>
                        <TableCell>{row.description || "-"}</TableCell>
                        <TableCell>{row.quantity || "-"}</TableCell>
                        <TableCell>{row.amount || "-"}</TableCell>
                        <TableCell>{row.paymentMode || "-"}</TableCell>
                        <TableCell>
                          <Stack spacing={0.4} alignItems="flex-start">
                            <Chip
                              label={row.isValid ? "Valid" : "Invalid"}
                              size="small"
                              sx={{ bgcolor: statusColors.bg, color: statusColors.fg, fontWeight: 700 }}
                            />
                            {!row.isValid ? (
                              <Typography sx={{ color: "#b91c1c", fontSize: 12.5 }}>
                                {row.errors.join(", ")}
                              </Typography>
                            ) : null}
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  })
                ) : (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 8, color: "#64748b" }}>
                      Upload a CSV or Excel file to preview rows here.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Stack direction="row" justifyContent="space-between" flexWrap="wrap" spacing={2}>
            <Button variant="outlined" onClick={() => { setUploadFile(null); setUploadPreview([]); }} sx={{ borderRadius: 3, px: 2.5, textTransform: "none", fontWeight: 800 }}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={importExpenses}
              disabled={!uploadStats.valid || uploading}
              sx={{ borderRadius: 3, px: 2.5, textTransform: "none", fontWeight: 800 }}
            >
              {uploading ? "Importing..." : "Import Expenses"}
            </Button>
          </Stack>
        </Stack>
      </Paper>
    </Stack>
  );

  const pageContent =
    activeSection === "create" ? renderCreatePage() : activeSection === "upload" ? renderUploadPage() : renderListPage();

  const shell = (
    <Stack spacing={2.5}>
      {!asPage ? (
        <Stack direction="row" spacing={1} flexWrap="wrap">
          {PAGE_SECTIONS.map((item) => (
            <Chip
              key={item.key}
              label={item.label}
              onClick={() => setActiveSection(item.key)}
              sx={{
                borderRadius: 99,
                bgcolor: activeSection === item.key ? "#dbeafe" : "#f8fafc",
                color: activeSection === item.key ? "#1d4ed8" : "#475569",
                fontWeight: 800,
              }}
            />
          ))}
        </Stack>
      ) : null}
      {pageContent}

      <Dialog open={Boolean(viewExpense)} onClose={() => setViewExpense(null)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 900 }}>Expense Details</DialogTitle>
        <DialogContent dividers>
          {viewExpense ? (
            <Stack spacing={1.4}>
              <Typography><strong>Vehicle:</strong> {viewExpense.vehicleNo}</Typography>
              <Typography><strong>Vehicle Name:</strong> {viewExpense.vehicleName}</Typography>
              <Typography><strong>Type:</strong> {viewExpense.type}</Typography>
              <Typography><strong>Date:</strong> {viewExpense.dateLabel}</Typography>
              <Typography><strong>Amount:</strong> {formatCurrency(viewExpense.amount)}</Typography>
              <Typography><strong>Quantity:</strong> {viewExpense.quantity || "-"}</Typography>
              <Typography><strong>Payment Mode:</strong> {viewExpense.paymentMode}</Typography>
              <Typography><strong>Description:</strong> {viewExpense.description}</Typography>
              {viewExpense.image ? (
                <Link href={viewExpense.image} target="_blank" rel="noreferrer">
                  Open receipt/image
                </Link>
              ) : null}
            </Stack>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewExpense(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(editState.id)} onClose={() => setEditState(getInitialEditState())} fullWidth maxWidth="md">
        <DialogTitle sx={{ fontWeight: 900 }}>Edit Expense</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr" }, gap: 2, mt: 0.5 }}>
            <Autocomplete
              options={deviceOptions}
              value={editState.vehicle}
              onChange={(_, value) => setEditState((prev) => ({ ...prev, vehicle: value }))}
              getOptionLabel={(option) => getVehicleLabel(option)}
              renderInput={(params) => <TextField {...params} label="Vehicle *" sx={inputSx} />}
            />
            <FormControl fullWidth sx={inputSx}>
              <InputLabel>Type *</InputLabel>
              <Select
                label="Type *"
                value={editState.type}
                onChange={(e) => setEditState((prev) => ({ ...prev, type: e.target.value }))}
              >
                {EXPENSE_TYPES.map((type) => (
                  <MenuItem key={type} value={type}>
                    {type}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Description"
              value={editState.description}
              onChange={(e) => setEditState((prev) => ({ ...prev, description: e.target.value }))}
              sx={inputSx}
            />
            <TextField
              type="date"
              label="Date *"
              InputLabelProps={{ shrink: true }}
              value={editState.date}
              onChange={(e) => setEditState((prev) => ({ ...prev, date: e.target.value }))}
              sx={inputSx}
            />
            <TextField
              label="Amount *"
              type="number"
              value={editState.amount}
              onChange={(e) => setEditState((prev) => ({ ...prev, amount: e.target.value }))}
              sx={inputSx}
            />
            <TextField
              label="Quantity"
              type="number"
              value={editState.quantity}
              onChange={(e) => setEditState((prev) => ({ ...prev, quantity: e.target.value }))}
              sx={inputSx}
            />
            <FormControl fullWidth sx={inputSx}>
              <InputLabel>Mode of Payment</InputLabel>
              <Select
                label="Mode of Payment"
                value={editState.paymentMode}
                onChange={(e) => setEditState((prev) => ({ ...prev, paymentMode: e.target.value }))}
              >
                {PAYMENT_MODES.map((modeValue) => (
                  <MenuItem key={modeValue} value={modeValue}>
                    {modeValue}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              type="file"
              inputProps={{ accept: "image/*,.pdf" }}
              onChange={(e) => setEditState((prev) => ({ ...prev, file: e.target.files?.[0] || null }))}
              sx={inputSx}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setEditState(getInitialEditState())}>Cancel</Button>
          <Button variant="contained" onClick={handleUpdateExpense} disabled={isUpdating}>
            {isUpdating ? "Updating..." : "Update Expense"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(deleteTarget)} onClose={() => setDeleteTarget(null)} fullWidth maxWidth="xs">
        <DialogTitle sx={{ fontWeight: 900 }}>Delete Expense</DialogTitle>
        <DialogContent dividers>
          <Typography sx={{ color: "#475569" }}>
            Are you sure you want to delete this expense entry? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDeleteExpense} disabled={isDeleting}>
            {isDeleting ? "Removing..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );

  if (asPage) {
    return shell;
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xl"
      PaperProps={{ sx: { borderRadius: 4, overflow: "hidden", minHeight: "86vh" } }}
    >
      <DialogTitle sx={{ px: 2.5, py: 2, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Box>
          <Typography sx={{ fontSize: 24, fontWeight: 900, color: "#0f172a" }}>Expense</Typography>
          <Typography sx={{ color: "#64748b", fontSize: 14.5 }}>{meta.subtitle}</Typography>
        </Box>
        <IconButton onClick={onClose}>
          <CloseRoundedIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ p: 2.5, bgcolor: "#f8fafc" }}>{shell}</DialogContent>
    </Dialog>
  );
};

export default ExpensePortalModal;
